import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Tabs } from '@artosera/ui';
import { useQueryClient } from '@tanstack/react-query';
import { accrochageApi, useEspaces, usePlans } from '../../api/accrochage.js';
import { useExpositions } from '../../api/expositions.js';
import { useMutation } from '../../api/mutation.js';
import { dansLaPortee, cleMetier } from '../../api/cles.js';
import type { AppelApi } from '../../api/client.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import styles from './Accrochage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function AccrochagePage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [params] = useSearchParams();
  const expositionFiltre = params.get('exposition') ?? undefined;
  const [onglet, setOnglet] = useState(expositionFiltre ? 'plans' : 'espaces');
  const [archive, setArchive] = useState(false);
  const [creation, setCreation] = useState(false);
  const [nom, setNom] = useState('');
  const [nature, setNature] = useState<'salle' | 'stand' | 'hors-les-murs'>('salle');
  const [expositionId, setExpositionId] = useState(expositionFiltre ?? '');
  const [espaceId, setEspaceId] = useState('');
  const espaces = useEspaces(galerieSlug, archive);
  const espacesActifs = useEspaces(galerieSlug);
  const plans = usePlans(galerieSlug, expositionFiltre);
  const expositions = useExpositions(galerieSlug, { inclureArchivees: true });
  const client = useQueryClient();
  const naviguer = useNavigate();
  const geste = useMutation(
    dansLaPortee({
      mutationFn: (executer: (appel: AppelApi) => Promise<unknown>, appel) => executer(appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(galerieSlug, 'accrochage') });
      },
    }),
  );
  const peutEcrire = galerieALaction(galerie, 'exhibitions:write');
  const titreExposition = (id: string) => {
    const titre = expositions.data?.expositions.find((e) => e.id === id)?.titre;
    return (i18n.language === 'en' ? titre?.en : titre?.fr) || titre?.fr || id;
  };
  async function creer() {
    if (onglet === 'espaces') {
      const resultat = (await geste.mutateAsync((appel) =>
        accrochageApi.creerEspace(galerieSlug, { nom, nature, lieuId: null, murs: [] }, appel),
      )) as Awaited<ReturnType<typeof accrochageApi.creerEspace>>;
      void naviguer(`/${galerieSlug}/accrochage/espaces/${resultat.espace.id}`);
    } else {
      const resultat = (await geste.mutateAsync((appel) =>
        accrochageApi.creerPlan(galerieSlug, { nom, espaceId, expositionId, axeCm: 155 }, appel),
      )) as Awaited<ReturnType<typeof accrochageApi.creerPlan>>;
      void naviguer(`/${galerieSlug}/accrochage/plans/${resultat.plan.id}`);
    }
  }
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete} data-ui="page-heading">
        <h1>{t('accrochage.titre')}</h1>
        <Verrouillable action="exhibitions:write" ouvert={peutEcrire}>
          <Button
            onClick={() => {
              geste.reset();
              setNom('');
              setCreation(true);
            }}
          >
            {t(onglet === 'espaces' ? 'accrochage.creerEspace' : 'accrochage.creerPlan')}
          </Button>
        </Verrouillable>
      </header>
      <Tabs
        value={onglet}
        onValueChange={setOnglet}
        tabs={['espaces', 'plans'].map((value) => ({
          value,
          label: t(`accrochage.${value}`),
          content: (
            <>
              {value === 'espaces' && (
                <label>
                  <input
                    type="checkbox"
                    checked={archive}
                    onChange={(e) => setArchive(e.target.checked)}
                  />{' '}
                  {t('accrochage.archives')}
                </label>
              )}
              {(value === 'espaces' ? espaces : plans).isPending && (
                <p role="status">{t('commun:chargement')}</p>
              )}
              {(value === 'espaces' ? espaces : plans).isError && (
                <p role="alert">{t('commun:erreurReseau')}</p>
              )}
              {value === 'espaces'
                ? espaces.data?.espaces.map((espace) => (
                    <article className={styles.panel} key={espace.id}>
                      <Link to={`/${galerieSlug}/accrochage/espaces/${espace.id}`}>
                        {espace.nom}
                      </Link>
                      <p>{t(`accrochage.natures.${espace.nature}`)}</p>
                      {peutEcrire && (
                        <div className={styles.actions}>
                          <Button
                            disabled={geste.isPending}
                            onClick={() =>
                              geste.mutate((appel) =>
                                accrochageApi.dupliquerEspace(
                                  galerieSlug,
                                  espace.id,
                                  espace.revision,
                                  appel,
                                ),
                              )
                            }
                          >
                            {t('accrochage.dupliquer')}
                          </Button>
                          <Button
                            disabled={geste.isPending}
                            onClick={() =>
                              geste.mutate((appel) =>
                                accrochageApi.archiverEspace(
                                  galerieSlug,
                                  espace.id,
                                  espace.revision,
                                  !espace.archive,
                                  appel,
                                ),
                              )
                            }
                          >
                            {t(espace.archive ? 'accrochage.restaurer' : 'accrochage.archiver')}
                          </Button>
                        </div>
                      )}
                    </article>
                  ))
                : [...new Set(plans.data?.plans.map((p) => p.expositionId))].map((id) => (
                    <section key={id} className={styles.panel}>
                      <h2>{titreExposition(id)}</h2>
                      {plans.data?.plans
                        .filter((p) => p.expositionId === id)
                        .map((plan) => (
                          <div className={styles.actions} key={plan.id}>
                            <Link to={`/${galerieSlug}/accrochage/plans/${plan.id}`}>
                              {plan.nom}
                            </Link>
                            <span>{t(`accrochage.etats.${plan.etat}`)}</span>
                            <Verrouillable action="exhibitions:write" ouvert={peutEcrire}>
                              <Button
                                disabled={geste.isPending}
                                onClick={() =>
                                  geste.mutate((appel) =>
                                    accrochageApi.dupliquerPlan(
                                      galerieSlug,
                                      plan.id,
                                      plan.revision,
                                      appel,
                                    ),
                                  )
                                }
                              >
                                {t('accrochage.variante')}
                              </Button>
                            </Verrouillable>
                          </div>
                        ))}
                    </section>
                  ))}
            </>
          ),
        }))}
      />
      {geste.isError && !creation && (
        <p role="alert">
          {geste.error instanceof Error ? geste.error.message : t('commun:erreurReseau')}
        </p>
      )}
      <Dialog
        open={creation}
        onOpenChange={setCreation}
        title={t(onglet === 'espaces' ? 'accrochage.creerEspace' : 'accrochage.creerPlan')}
        closeLabel={t('commun:actions.fermer')}
      >
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void creer().catch(() => {
              /* La mutation affiche l’erreur. */
            });
          }}
        >
          <Field htmlFor="acc-nom" label={t('accrochage.nom')}>
            <Input
              id="acc-nom"
              required
              maxLength={120}
              value={nom}
              onChange={(e) => setNom(e.target.value)}
            />
          </Field>
          {onglet === 'espaces' ? (
            <Field htmlFor="acc-nature" label={t('accrochage.nature')}>
              <select
                id="acc-nature"
                value={nature}
                onChange={(e) => setNature(e.target.value as typeof nature)}
              >
                {['salle', 'stand', 'hors-les-murs'].map((n) => (
                  <option value={n} key={n}>
                    {t(`accrochage.natures.${n}`)}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <>
              <Field htmlFor="acc-exposition" label={t('accrochage.exposition')}>
                <select
                  required
                  id="acc-exposition"
                  value={expositionId}
                  onChange={(e) => setExpositionId(e.target.value)}
                >
                  <option value="">—</option>
                  {expositions.data?.expositions
                    .filter((e) => !e.archive)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {titreExposition(e.id)}
                      </option>
                    ))}
                </select>
              </Field>
              <Field htmlFor="acc-espace" label={t('accrochage.espace')}>
                <select
                  required
                  id="acc-espace"
                  value={espaceId}
                  onChange={(e) => setEspaceId(e.target.value)}
                >
                  <option value="">—</option>
                  {espacesActifs.data?.espaces.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nom}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          {geste.isError && (
            <p role="alert">
              {geste.error instanceof Error ? geste.error.message : t('commun:erreurReseau')}
            </p>
          )}
          <Button type="submit" loading={geste.isPending}>
            {t('accrochage.creer')}
          </Button>
        </form>
      </Dialog>
    </section>
  );
}
